/**
 * Reglas de casa congeladas por pelea (`rules_snapshot`).
 *
 * Defecto F2 del HTML: el rake era global y retroactivo. Aquí el snapshot es inmutable:
 * cambiarlo afecta SOLO peleas futuras. Es un JSONB congelado al crear la pelea y
 * registrado en la auditoría junto con el `engine_version` que lo produjo.
 */
import { z } from 'zod';
import { CentsWireSchema, CurrencySchema, OddsBpSchema, RakeBpSchema } from '../money.js';

/** Política ante pool desbalanceado (§8.1.4). */
export const PoliticaDesbalanceSchema = z.enum([
  /** Rechaza apuestas al lado vacío hasta que exista siembra. */
  'rechazar_lado_vacio',
  /** La casa aporta escrow para honrar el piso de cuota. */
  'piso_fondeado',
  /** Acepta cualquier desbalance, aplicando tope de pago. */
  'aceptar_con_tope',
]);
export type PoliticaDesbalance = z.infer<typeof PoliticaDesbalanceSchema>;

export const RulesSnapshotSchema = z.object({
  presetId: z.string().min(3).max(64),
  presetSemver: z.string().regex(/^\d+\.\d+\.\d+$/, 'semver esperado x.y.z'),
  /** Versión del motor puro que calculó: reproducible y auditable (§8.3). */
  engineVersion: z.string().min(1),
  /** Hash de los parámetros efectivos: permite detectar divergencias. */
  paramsHash: z.string().min(8),
  rakeBp: RakeBpSchema,
  currency: CurrencySchema,
  /** Piso de cuota en bp (p.ej. 10500 = 1.05x). 0 = sin piso. */
  pisoBp: OddsBpSchema.or(z.literal(0)),
  /** Tope de cuota en bp (p.ej. 500000 = 50x). 0 = sin tope. */
  topeBp: OddsBpSchema.or(z.literal(0)),
  /** Liquidez LMSR (`b`). 0 si el preset no la usa. */
  liquidezB: z.number().nonnegative(),
  politicaDesbalance: PoliticaDesbalanceSchema,
  minStakeCents: CentsWireSchema,
  maxStakeCents: CentsWireSchema,
  /** Exposición máxima por lado, en centavos. 0 = sin límite. */
  maxExposureCents: CentsWireSchema,
  /** Escrow que la casa compromete para honrar el piso (piso_fondeado). */
  houseEscrowCents: CentsWireSchema,
  /** Límite de exposición por apostador vía Kelly (0 = desactivado). */
  kellyCapFraction: z.number().min(0).max(1),
  /**
   * Cuotas fijas publicadas por la casa (presets `book` / `book_vig`).
   * Null en presets pari-mutuel, donde la cuota se deriva del pozo.
   */
  fixedOdds: z
    .object({ redBp: OddsBpSchema, blueBp: OddsBpSchema })
    .nullable()
    .default(null),
});
export type RulesSnapshot = z.infer<typeof RulesSnapshotSchema>;

export const DEFAULT_RULES: RulesSnapshot = {
  presetId: 'PIT-TOT-2W',
  presetSemver: '1.0.0',
  engineVersion: '1.0.0',
  paramsHash: 'default000',
  rakeBp: 500,
  currency: { code: 'USD', symbol: '$', decimals: 2 },
  pisoBp: 10_500,
  topeBp: 0,
  liquidezB: 0,
  politicaDesbalance: 'rechazar_lado_vacio',
  minStakeCents: 100,
  maxStakeCents: 10_000_00,
  maxExposureCents: 0,
  houseEscrowCents: 0,
  kellyCapFraction: 0,
  fixedOdds: null,
};

